import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  Alert,
  AlertStatus,
  AlertType,
  DeviceType,
  MuteStationRequest,
} from '@monitor/alerting/data/alerting.models';
import { AlertingFacade } from '@monitor/alerting/ngrx/alerting.facade';
import { TranslateService } from '@ngx-translate/core';
import {
  MuteStationAlertsModalComponent,
  MuteStationModalConfig,
} from '@shared/components/mute-station-alerts-modal/mute-station-alerts-modal.component';
import {
  UnmuteStationAlertsModalComponent,
  UnmuteStationModalConfig,
} from '@shared/components/unmute-station-alerts-modal/unmute-station-alerts-modal.component';
import { LocationTypes } from '@shared/pipes/location.pipe';
import { ButtonColor, ButtonSize } from '@widgets/eop-button/eop-button.component';
import { IconColor, IconSize } from '@widgets/eop-icon/eop-icon.component';
import { ChipColor, ChipSize } from '@widgets/eop-chip/data/eop-chip.data';
import { ModalService } from '@widgets/modal/modal.service';
import { take } from 'rxjs/operators';

export enum MenuAction {
  MARK_AS_RESOLVED = 'MARK_AS_RESOLVED',
  MUTE = 'MUTE',
  UNMUTE = 'UNMUTE',
  SHOW_IN_MUTED = 'SHOW_IN_MUTED',
}

@Component({
  selector: 'eop-alert-dashboard-alert',
  templateUrl: './alert-dashboard-alert.component.html',
  styleUrl: './alert-dashboard-alert.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AlertDashboardAlertComponent implements OnChanges {
  alertingFacade = inject(AlertingFacade);

  readonly IconSize = IconSize;
  readonly IconColor = IconColor;
  readonly ChipColor = ChipColor;
  readonly ChipSize = ChipSize;
  readonly AlertStatus = AlertStatus;
  readonly DeviceType = DeviceType;
  readonly ButtonColor = ButtonColor;
  readonly LocationTypes = LocationTypes;
  menuItems = [];

  @Input({ required: true }) alert: Alert;
  @Input({ required: true }) hasManagePermission: boolean;
  @Input({ required: true }) partnerId: string;

  protected readonly AlertType = AlertType;
  protected readonly ButtonSize = ButtonSize;

  private readonly translateService = inject(TranslateService);
  private readonly modalService = inject(ModalService);
  private readonly router = inject(Router);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.alert) {
      this.initializeMenuItems();
    }
  }

  onSelectAlert(event: Event): void {
    const target = event.target as HTMLElement;
    if (target?.classList.contains('vaylens-icon-more-3-filled')) {
      // more actions button clicked
      return;
    }
    if (target?.closest('.alert-details')) {
      // ignore clicks inside alert detail containers
      return;
    }
    this.alertingFacade.selectAlert(this.alert.id, this.partnerId, this.alert.alertType);
  }

  onMenuItemSelected(alert: Alert, action: string): void {
    const actionType = MenuAction[action];
    switch (actionType) {
      case MenuAction.MARK_AS_RESOLVED:
        this.alertingFacade.maskAsResolved(alert.id, this.partnerId);
        break;
      case MenuAction.MUTE:
        this.openMuteStationDialog();
        break;
      case MenuAction.UNMUTE:
        this.openUnmuteStationDialog();
        break;
      case MenuAction.SHOW_IN_MUTED:
        this.router.navigate(['/monitor/alerting'], {
          queryParams: { activeTab: 1, stationId: alert.deviceId, partnerId: this.partnerId },
        });
        break;
      default:
        console.warn('Unknown action type:', actionType);
    }
  }

  isEvDriverIncident(): boolean {
    return this.alert.alertType === AlertType.SALESFORCE_TICKET;
  }

  isSessionAlert(): boolean {
    return this.alert.alertType === AlertType.CHARGING_SESSION;
  }

  getStationDetailsParams(
    alert: Pick<Alert, 'chargingStationId' | 'deviceId'>,
    type: DeviceType
  ): {
    uuid: string;
    chargePort?: string;
  } {
    const params: {
      uuid: string;
      chargePort?: string;
    } = { uuid: alert.chargingStationId };
    if (type === DeviceType.EVSE) {
      params.chargePort = alert.deviceId;
    }
    return params;
  }

  get alertIcon(): string {
    switch (this.alert.alertType) {
      case AlertType.SALESFORCE_TICKET:
        return 'messages-2-outlined';
      case AlertType.CHARGING_SESSION:
        return 'activity-down-outlined';
      default:
        return 'warning-1-outlined';
    }
  }

  private openMuteStationDialog(): void {
    const config: MuteStationModalConfig = {
      stationName: this.alert.chargingStationName,
      country: this.alert.address.country,
      zipCode: this.alert.address.zipCode,
      city: this.alert.address.city,
      street: this.alert.address.street,
    };
    this.modalService
      .open(MuteStationAlertsModalComponent, {
        data: config,
      })
      .close$.pipe(take(1))
      .subscribe((muteStation: MuteStationRequest) => {
        if (muteStation) {
          this.alertingFacade.muteStation(
            this.alert.chargingStationId,
            this.partnerId,
            muteStation
          );
        }
      });
  }

  private openUnmuteStationDialog(): void {
    const config: UnmuteStationModalConfig = {
      stationName: this.alert.chargingStationName,
      country: this.alert.address.country,
      zipCode: this.alert.address.zipCode,
      city: this.alert.address.city,
      street: this.alert.address.street,
    };

    this.modalService
      .open(UnmuteStationAlertsModalComponent, { data: config })
      .close$.pipe(take(1))
      .subscribe(confirmed => {
        if (confirmed) {
          this.alertingFacade.unmuteStation(this.alert.chargingStationId, this.partnerId);
        }
      });
  }

  private initializeMenuItems(): void {
    this.menuItems = [];
    if (this.alert.alertStatus === AlertStatus.ACTIVE) {
      if (this.isEvDriverIncident()) {
        this.menuItems.push({
          id: MenuAction.MARK_AS_RESOLVED,
          label: this.translateService.instant('ALERTING.MARK_AS_RESOLVED'),
          icon: 'check-circle-outlined',
        });
      } else {
        if (!this.alert.isStationMuted) {
          this.menuItems.push({
            id: MenuAction.MUTE,
            label: this.translateService.instant('ALERTING.MUTE_STATION'),
            icon: 'volume-mute-outlined',
          });
        }
      }
    }
    if (!this.isEvDriverIncident()) {
      if (this.alert.isStationMuted) {
        this.menuItems.push({
          id: MenuAction.UNMUTE,
          label: this.translateService.instant('ALERTING.UNMUTE_STATION'),
          icon: 'volume-high-outlined',
        });
        this.menuItems.push({
          id: MenuAction.SHOW_IN_MUTED,
          label: this.translateService.instant('ALERTING.SHOW_IN_MUTED'),
          icon: 'arrow-right-outlined',
        });
      }
    }
  }
}
